import {
  cn,
  PageHeader,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@platform/ui";
import { useSession } from "@platform/ui/features";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslations } from "use-intl";
import { type Assignee, QueueFrame, QueueToolbar, TargetCell } from "../components/queue";
import { locale } from "../lib/i18n";
import { VerificationCasePage } from "./verification-case";

type State = "awaiting_review" | "awaiting_provider" | "decided";

interface CaseRow {
  readonly id: string;
  readonly enterprise: {
    readonly legalName: string;
    readonly country: string;
    readonly registrationNumber: string;
    readonly displayName: { en: string; zh?: string | undefined };
  };
  readonly provider: "sumsub" | "manual";
  readonly providerResult: { readonly answer: "GREEN" | "RED" | null } | null;
  readonly reviewStartedAt: string | null;
  readonly assigneeOperatorId: string | null;
  readonly decision: string | null;
  readonly decidedAt: string | null;
  readonly createdAt: string;
}

/**
 * The verification queue (ADM-2, OPS-1, card 114): a dense table of cases, oldest first, with each one's Sumsub
 * result, time against the 24-hour decision target (card 117), and assignee; the case open beside it, as the
 * canvas's queue board shows. The clock starts when the review does, and the API lists by it (card 119).
 */
export function VerificationQueuePage() {
  const t = useTranslations();
  const { caseId } = useParams();
  const { session, state: auth } = useSession();
  const me = auth.status === "signed_in" ? auth.claims.sub : undefined;
  const [state, setState] = useState<State>("awaiting_review");
  const [assignee, setAssignee] = useState<Assignee>("any");
  const [rows, setRows] = useState<readonly CaseRow[] | undefined>();
  const [version, setVersion] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: read again after a decision (`version`)
  useEffect(() => {
    void session.api
      .GET("/v1/ops/verification-cases", { params: { query: { state, assignee } } })
      .then(({ data }) => {
        setRows(data?.items ?? []);
      });
  }, [session, state, assignee, version]);

  const regions = new Intl.DisplayNames([locale === "zh" ? "zh-CN" : "en"], { type: "region" });
  // A case verified by hand has no provider result: the reviewer's checks are the result (ADR 0026).
  const answer = (row: CaseRow) =>
    row.provider === "manual"
      ? "byHand"
      : row.providerResult?.answer === "GREEN"
        ? "passed"
        : row.providerResult?.answer === "RED"
          ? "failed"
          : "noResult";
  const assigned = (row: CaseRow) =>
    t(
      row.assigneeOperatorId === null
        ? "admin.queue.nobody"
        : row.assigneeOperatorId === me
          ? "admin.queue.you"
          : "admin.queue.someone",
    );

  return (
    <section aria-labelledby="queue-title" className="flex flex-col gap-5">
      <PageHeader id="queue-title" title={t("admin.queue.title")} description={t("admin.queue.intro")} />
      <QueueToolbar
        id="queue"
        states={["awaiting_review", "awaiting_provider", "decided"] as const}
        state={state}
        onState={(next) => {
          setRows(undefined);
          setState(next);
        }}
        stateName={(each) => t(`admin.queue.${each}`)}
        assignee={assignee}
        onAssignee={(next) => {
          setRows(undefined);
          setAssignee(next);
        }}
      />
      <div
        className={cn("grid items-start gap-5", caseId !== undefined && "xl:grid-cols-[minmax(0,1fr)_400px]")}
      >
        <QueueFrame rows={rows}>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t("admin.queue.enterprise")}</TableHeaderCell>
                <TableHeaderCell>{t("admin.queue.country")}</TableHeaderCell>
                <TableHeaderCell>{t("admin.queue.sumsub")}</TableHeaderCell>
                <TableHeaderCell>
                  {t(state === "decided" ? "admin.target.decidedColumn" : "admin.target.column")}
                </TableHeaderCell>
                <TableHeaderCell>{t("admin.queue.assignee")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row) => (
                <TableRow
                  key={row.id}
                  className={row.id === caseId ? "bg-brand-50 hover:bg-brand-50" : undefined}
                  aria-current={row.id === caseId ? "true" : undefined}
                >
                  <TableCell>
                    <Link
                      className="font-medium text-ink hover:text-brand-600"
                      to={`/verification/${row.id}`}
                    >
                      {row.enterprise.legalName}
                    </Link>
                    <span className="block text-caption text-ink-muted tabular-nums">
                      {row.enterprise.registrationNumber}
                    </span>
                  </TableCell>
                  <TableCell>{regions.of(row.enterprise.country) ?? row.enterprise.country}</TableCell>
                  <TableCell>
                    <StatusBadge
                      label={t(`admin.queue.${answer(row)}`)}
                      tone={
                        answer(row) === "passed" ? "done" : answer(row) === "failed" ? "problem" : "neutral"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TargetCell startedAt={row.reviewStartedAt} decidedAt={row.decidedAt} />
                  </TableCell>
                  <TableCell className="text-ink-muted">{assigned(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </QueueFrame>
        {caseId !== undefined && (
          <aside className="xl:sticky xl:top-5">
            <VerificationCasePage key={caseId} onDecided={() => setVersion((current) => current + 1)} />
          </aside>
        )}
      </div>
    </section>
  );
}
